/**
 * Section Morph Adapter (factory)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Ported from shopify-framework (issue #20 cart-engine migration). Generalized
 * "section morph on event" adapter. The cart engine is the first consumer.
 *
 * The morph itself (Alpine.morph + Section Rendering API) already lives in
 * assets/section-renderer.js. What is domain-specific is only:
 *   1. a registry of section ids to re-render on a given event, and
 *   2. a listener on that event that hands `event.detail.sections` to the morph.
 *
 * Usage (the cart engine wires this in connectedCallback):
 *   const adapter = window.Theme.createMorphAdapter({
 *     eventName:        window.Theme.events.NAMES.cartUpdate,  // 'store:cart:update'
 *     sectionAttribute: 'data-cart-section',
 *   });
 *   adapter.register('cart-drawer-id');
 *   adapter.getIds();  // ['cart-drawer-id', ...]  ← engine sends as ?sections=
 *
 * Auto-discovery: every `[sectionAttribute]` element present at boot has its
 * closest `.shopify-section` ancestor registered (id stripped of the
 * `shopify-section-` prefix). Surfaces added after boot must call
 * `adapter.register(id)` (design-mode logs a warning when one appears
 * unregistered).
 *
 * When `eventName` fires, the adapter forwards `event.detail.sections` (a
 * `{ sectionId: html }` object from the cart POST response) to
 * `window.Theme.morphSections`, resolved lazily at dispatch time so
 * section-renderer.js (an ES module) can load after this IIFE.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  window.Theme = window.Theme || {};

  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };

  function createMorphAdapter({ eventName, sectionAttribute } = {}) {
    if (typeof eventName !== 'string' || !eventName) {
      throw new Error('[section-morph-adapter] eventName is required');
    }
    if (typeof sectionAttribute !== 'string' || !sectionAttribute) {
      throw new Error('[section-morph-adapter] sectionAttribute is required');
    }

    const log = Logger.ns(`morph-adapter:${eventName}`);
    const registry = new Set();

    function register(sectionId) {
      if (typeof sectionId === 'string' && sectionId) registry.add(sectionId);
    }
    function unregister(sectionId) {
      if (typeof sectionId === 'string' && sectionId) registry.delete(sectionId);
    }
    function getIds() {
      return Array.from(registry);
    }

    function discover(root = document) {
      root.querySelectorAll(`[${sectionAttribute}]`).forEach((el) => {
        const sectionEl = el.closest('.shopify-section');
        if (!sectionEl || !sectionEl.id) return;
        const id = sectionEl.id.replace('shopify-section-', '');
        if (id) register(id);
      });
    }

    // Boot-time auto-discovery.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => discover(), { once: true });
    } else {
      discover();
    }

    // Forward the sections payload to the universal morph utility. Resolve
    // morphSections at dispatch time so section-renderer.js can load later.
    document.addEventListener(eventName, (event) => {
      const sections = event && event.detail && event.detail.sections;
      if (!sections || typeof sections !== 'object') return;
      const morphSections = window.Theme && window.Theme.morphSections;
      if (typeof morphSections !== 'function') {
        log.warn('Theme.morphSections not available yet — section-renderer.js missing?');
        return;
      }
      // `data.addedKeys` (set by cart-engine on an add) scopes the enter
      // animation to the lines that add.js actually created.
      const addedKeys = event.detail.data && event.detail.data.addedKeys;
      morphSections(sections, { enterKeys: Array.isArray(addedKeys) ? addedKeys : [] });
    });

    // Dev-mode mutation warning (theme editor only).
    if (window.Shopify && window.Shopify.designMode && typeof MutationObserver === 'function') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== 'childList') return;
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (!node.hasAttribute || !node.hasAttribute(sectionAttribute)) return;
            const sectionEl = node.closest && node.closest('.shopify-section');
            if (!sectionEl || !sectionEl.id) return;
            const id = sectionEl.id.replace('shopify-section-', '');
            if (id && !registry.has(id)) {
              console.warn(
                `[morph-adapter:${eventName}] Section "${id}" added with [${sectionAttribute}] but not registered. ` +
                  `Call adapter.register("${id}") if it should receive morph updates.`
              );
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return { register, unregister, getIds };
  }

  window.Theme.createMorphAdapter = createMorphAdapter;
})();
